import { useState } from 'react';
import { 
  EmailAuthProvider, 
  linkWithCredential, 
  updateProfile 
} from 'firebase/auth';
import { auth } from '../lib/firebase/config';
import UserProfileService from '../services/UserProfileService';

interface AccountUpgradeData {
  email: string;
  password: string;
  displayName?: string;
}

interface UseAccountUpgradeResult {
  isLoading: boolean;
  error: string | null;
  success: boolean;
  upgradeAccount: (data: AccountUpgradeData) => Promise<boolean>;
  resetState: () => void;
}

export function useAccountUpgrade(): UseAccountUpgradeResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetState = () => {
    setError(null);
    setSuccess(false);
    setIsLoading(false);
  };

  const validateAccountUpgradeData = (data: AccountUpgradeData): string | null => {
    if (!data.email || !data.password) {
      return 'Email and password are required';
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      return 'Please enter a valid email address';
    }

    if (data.password.length < 6) {
      return 'Password must be at least 6 characters';
    }

    return null;
  };

  const upgradeAccount = async (data: AccountUpgradeData): Promise<boolean> => {
    setError(null);
    setSuccess(false);
    
    // Check if user is anonymous
    const user = auth.currentUser;
    if (!user) {
      setError('No user is signed in');
      return false;
    }
    
    if (!user.isAnonymous) {
      setError('This account is already upgraded');
      return false;
    }

    // Validate input data
    const validationError = validateAccountUpgradeData(data);
    if (validationError) {
      setError(validationError);
      return false;
    }

    setIsLoading(true);

    try {
      // Create credential with email and password
      const credential = EmailAuthProvider.credential(data.email, data.password);
      
      // Link anonymous account with credential
      await linkWithCredential(user, credential);
      
      // Update profile if display name is provided
      if (data.displayName) {
        await updateProfile(user, {
          displayName: data.displayName
        });
      }

      // Update user profile in Firestore
      await UserProfileService.syncUserProfile({
        uid: user.uid,
        email: data.email,
        displayName: data.displayName || null,
        photoURL: user.photoURL,
        isAnonymous: false
      });

      setSuccess(true);
      return true;
    } catch (error: any) {
      let errorMessage = 'Failed to upgrade account';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already in use by another account';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    success,
    upgradeAccount,
    resetState
  };
} 